// Deterministic avatar: initials + consistent colour derived from name or role.
// Use role colours when the role is known; otherwise hash the name for variety.

export type AvatarRole = 'Customer' | 'Carrier' | 'Workshop' | 'Admin' | 'Ops';
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const ROLE_COLOUR: Record<AvatarRole, string> = {
  Customer: 'bg-teal-500    text-white',
  Carrier:  'bg-emerald-600 text-white',
  Workshop: 'bg-amber-500   text-white',
  Admin:    'bg-slate-700   text-white',
  Ops:      'bg-slate-500   text-white',
};

const HASH_COLOURS = [
  'bg-teal-500    text-white',
  'bg-emerald-600 text-white',
  'bg-violet-500  text-white',
  'bg-amber-500   text-white',
  'bg-rose-500    text-white',
  'bg-pink-500    text-white',
  'bg-cyan-500    text-white',
  'bg-indigo-500  text-white',
];

const SIZE_CLS: Record<AvatarSize, string> = {
  xs: 'w-6  h-6  text-[10px]',
  sm: 'w-8  h-8  text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-14 h-14 text-lg',
};

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = Math.imul(31, h) + s.charCodeAt(i) | 0; }
  return Math.abs(h);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface UserAvatarProps {
  name: string;
  role?: AvatarRole;
  size?: AvatarSize;
  className?: string;
}

export function UserAvatar({ name, role, size = 'sm', className = '' }: UserAvatarProps) {
  const colour = role
    ? ROLE_COLOUR[role]
    : HASH_COLOURS[hashStr(name) % HASH_COLOURS.length];

  return (
    <div
      className={`rounded-full flex items-center justify-center font-bold shrink-0 select-none
        ${SIZE_CLS[size]} ${colour} ${className}`}
      title={name}
      aria-label={name}
    >
      {initials(name)}
    </div>
  );
}
